<script setup>
import { useAlerts } from '@/store/alerts.js'
import { useI18n } from 'vue-i18n';
import { getLocalData } from './utils/localdata';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { changeLanguage } from '@/plugins/i18n/index'
import NetworkStatus from '@/views/components/NetworkStatus.vue';
import MaintenanceScreen from '@/views/components/MaintenanceScreen.vue';
import axios from '@/plugins/axios.js';

const router = useRouter();
const { locale } = useI18n();
const isNative = ref(window.Capacitor?.isNativePlatform?.() ?? false);

// Maintenance mode
const isInMaintenance = ref(false);
const maintenanceMessage = ref('Estamos mejorando Kiwish para ti');

const checkMaintenance = async () => {
  try {
    const response = await axios.get('/maintenance/check');
    if (response.data?.maintenance) {
      isInMaintenance.value = true;
      maintenanceMessage.value = response.data.message || 'Estamos mejorando Kiwish para ti';
    }
  } catch (e) {
    // Si falla la llamada, asumimos que no hay mantenimiento
    console.log('Maintenance check failed, continuing normally');
  }
};

onMounted(async () => {
  // Verificar mantenimiento primero
  await checkMaintenance();

  // Agregar clase al body para detectar si es nativa
  if (isNative.value) {
    document.body.classList.add('native-app');
  } else {
    document.body.classList.add('web-app');
  }

  if (!await getLocalData('lang')) router.push({path: '/lang'});
  else {
    changeLanguage(await getLocalData('lang'))
  }
});

const destroyAlert = () => {
  useAlerts().alert.visible = false;
};
</script>
<template>
  <div class="app-container">
    <ion-app>
      <!-- Pantalla de mantenimiento -->
      <MaintenanceScreen v-if="isInMaintenance" :message="maintenanceMessage" />

      <NetworkStatus />
      <ion-router-outlet animated="false"></ion-router-outlet>
      <ion-toast
        :is-open="useAlerts().alert.visible"
        :class="useAlerts().alert.class"
        position="bottom"
        swipe-gesture="vertical"
        :message="useAlerts().alert.message ? $t(useAlerts().alert.message) : ''"
        :duration="3000"
        @didDismiss="destroyAlert()"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      />
    </ion-app>
  </div>
</template>

<style>
  /* Contenedor principal */
  .app-container {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    background: #283618 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .app-container ion-app {
    width: 100% !important;
    height: 100% !important;
    overflow: hidden !important;
    position: relative !important;
  }

  /* En desktop, limitar tamaño y centrar */
  @media (min-width: 769px) {
    .app-container ion-app {
      max-width: 430px !important;
      max-height: 932px !important;
      border-radius: 20px !important;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3) !important;
    }
  }

  /* Estilos para toast - usar absolute dentro de ion-app en lugar de fixed */
  ion-toast {
    position: absolute !important;
    bottom: calc(70px + env(safe-area-inset-bottom, 0px)) !important;
    left: 0 !important;
    right: 0 !important;
    z-index: 99999 !important;
    /* Evitar que afecte el layout */
    pointer-events: none;
  }

  ion-toast::part(container) {
    pointer-events: auto;
  }

  /* Para Safari web iOS específicamente - más espacio */
  .web-app ion-toast {
    bottom: calc(70px + 30px + env(safe-area-inset-bottom, 0px)) !important;
  }

  /* Para app nativa */
  .native-app ion-toast {
    bottom: calc(70px + env(safe-area-inset-bottom, 0px)) !important;
  }

  ion-toast.alert-success {
    --background: #6DA471;
    --box-shadow: 3px 3px 10px 0 rgba(0, 0, 0, 0.2);
    --color: #fff;
    text-align: center;
  }

  ion-toast.alert-error {
    --background: #a9071a;
    --box-shadow: 3px 3px 10px 0 rgba(0, 0, 0, 0.2);
    --color: #fff;
    text-align: center;
  }

</style>